// frontend/src/components/planner/ForecastPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  TrendingUp,
  CreditCard,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Circle,
  Ban,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  GripVertical,
  Plus,
  RotateCcw,
  X,
  FileText,
  Trash2,
  Building2,
  Clock,
  Landmark,
  ArrowLeftRight,
  Repeat,
  Zap,
  Camera,
  Unlink
} from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';
import ReceiptViewer from '../common/ReceiptViewer';
import QuickAddModal from './QuickAddModal';
import AddRecurringModal from './AddRecurringModal';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';
import { useSync } from '../../contexts/SyncContext';

// Status configuration
const STATUS_CONFIG = {
  pending: {
    icon: Circle,
    label: 'Upcoming',
    color: 'text-gray-400',
    bg: 'bg-gray-100',
    border: 'border-gray-300'
  },
  overdue: {
    icon: AlertCircle,
    label: 'Overdue',
    color: 'text-red-600',
    bg: 'bg-red-100',
    border: 'border-red-300'
  },
  paid: {
    icon: CheckCircle2,
    label: 'Paid',
    tooltip: 'Paid: I paid this, but it is not yet reflected in my bank balance',
    color: 'text-emerald-600',
    bg: 'bg-emerald-100',
    border: 'border-emerald-300'
  },
  settled: {
    icon: Landmark,
    label: 'Settled',
    tooltip: 'Settled: Bank has processed this and it is reflected in my balance, but no transaction has synced yet',
    color: 'text-teal-600',
    bg: 'bg-teal-100',
    border: 'border-teal-300'
  },
  cleared: {
    icon: CheckCircle2,
    label: 'Cleared',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-300'
  },
  skipped: {
    icon: Ban,
    label: 'Skipped',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    border: 'border-amber-300'
  }
};

// Item type configuration
const TYPE_CONFIG = {
  bill: {
    icon: Calendar,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    amountColor: 'text-red-600'
  },
  debt: {
    icon: CreditCard,
    color: 'text-red-600',
    bg: 'bg-red-50',
    amountColor: 'text-red-600'
  },
  income: {
    icon: TrendingUp,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    amountColor: 'text-emerald-600'
  }
};

export default function ForecastPanel() {
  const { filterParam, activeFilter, entities } = useBusinessEntity();
  const { lastSyncAt } = useSync();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forecast, setForecast] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [accountsData, setAccountsData] = useState({}); // Account balances from forecast response
  const [accountsList, setAccountsList] = useState([]); // List for dropdown
  const [selectedAccountId, _setSelectedAccountId] = useState(() => {
    try { return localStorage.getItem('stb_forecastAccountId') || ''; } catch { return ''; }
  });
  const setSelectedAccountId = React.useCallback((val) => {
    _setSelectedAccountId(val);
    try { localStorage.setItem('stb_forecastAccountId', val); } catch { /* noop */ }
  }, []);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateValue, setEditingDateValue] = useState('');
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [editingAmountValue, setEditingAmountValue] = useState('');
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddDate, setQuickAddDate] = useState(null);
  const [showAddRecurring, setShowAddRecurring] = useState(false);
  const [receiptViewerTxId, setReceiptViewerTxId] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [envelopeSpending, setEnvelopeSpending] = useState({});
  const [categories, setCategories] = useState([]);
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const datePickerRef = useRef(null);
  const amountPickerRef = useRef(null);
  const notesPickerRef = useRef(null);
  const addDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Track forecast view for onboarding checklist
  useEffect(() => {
    try {
      localStorage.setItem('stb_forecast_viewed', 'true');
    } catch (err) {
      // Ignore localStorage errors (private browsing, quota exceeded)
    }
  }, []); // Run once on mount

  // Load accounts list and categories on mount
  useEffect(() => {
    loadAccounts();
    api.get('/api/household/categories').then(res => {
      if (res.data?.categories) setCategories(res.data.categories);
    }).catch(() => {});
  }, []);

  // Validate stored selectedAccountId still exists after accounts load
  useEffect(() => {
    if (accountsList.length > 0 && selectedAccountId && !accountsList.some(a => String(a.id) === String(selectedAccountId))) {
      setSelectedAccountId('');
    }
  }, [accountsList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadForecast();
  }, [selectedAccountId, filterParam]); // Reload when account or entity filter changes

  // Auto-refresh forecast when global sync completes
  useEffect(() => {
    if (lastSyncAt) loadForecast();
  }, [lastSyncAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize all weeks as expanded
  useEffect(() => {
    if (forecast.length > 0) {
      const weeks = groupByWeek(forecast);
      const initialExpanded = {};
      Object.keys(weeks).forEach(weekKey => {
        initialExpanded[weekKey] = true;
      });
      setExpandedWeeks(initialExpanded);
    }
  }, [forecast]);

  async function loadAccounts() {
    try {
      const response = await api.get('/api/planner/accounts');
      if (response.data.success) {
        setAccountsList(response.data.accounts || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }

  async function loadForecast(regenerate = false) {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams();
      if (regenerate) params.append('regenerate', 'true');
      params.append('withBalances', 'true');
      if (selectedAccountId) {
        params.append('bankAccountId', selectedAccountId);
      }
      if (filterParam) {
        params.append('businessEntityId', filterParam);
      }
      
      const response = await api.get(`/api/forecast?${params.toString()}`);
      
      if (response.data.success) {
        // Backend now handles filtering by bankAccountId - no client-side filter needed
        const forecastData = response.data.data || [];
        
        setForecast(forecastData);
        setBalanceSummary(response.data.balanceSummary || null);
        setAccountsData(response.data.accounts || {});

        // Fetch envelope spending if any envelope items exist
        const hasEnvelopes = forecastData.some(item => item.is_envelope);
        if (hasEnvelopes) {
          try {
            const envRes = await api.get('/api/forecast/envelope-spending');
            if (envRes.data.success) {
              setEnvelopeSpending(envRes.data.envelopes || {});
            }
          } catch (envErr) {
            console.error('Error loading envelope spending:', envErr);
          }
        } else {
          setEnvelopeSpending({});
        }
      } else {
        setError(response.data.error || 'Failed to load forecast');
      }
    } catch (err) {
      console.error('Error loading forecast:', err);
      setError('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(instanceId, newStatus) {
    try {
      setUpdatingId(instanceId);
      
      await api.patch(`/api/forecast/${instanceId}/status`, { status: newStatus });
      
      // Reload to get updated balances
      await loadForecast();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleUnclear(item) {
    if (!window.confirm('Undo bank match? This item will return to "pending" status.')) return;
    try {
      setUpdatingId(item.id);
      const res = await api.post('/api/planner/unmatch', { scheduledTransactionId: item.id });
      if (!res.data?.success) throw new Error(res.data?.error || 'Unmatch failed');
      await loadForecast();
    } catch (err) {
      console.error('Error un-clearing:', err);
      alert(err?.response?.data?.error || err?.message || 'Failed to undo match');
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleReschedule(instanceId, newDate) {
    try {
      setUpdatingId(instanceId);
      
      await api.patch(`/api/forecast/${instanceId}`, { scheduled_date: newDate });
      
      await loadForecast();
    } catch (err) {
      console.error('Error rescheduling:', err);
      alert('Failed to reschedule item');
    } finally {
      setUpdatingId(null);
      setEditingDateId(null);
    }
  }

  // Reset to original date
  async function handleResetToOriginal(item) {
    if (item.original_date && item.original_date !== item.scheduled_date) {
      await handleReschedule(item.id, item.original_date);
    }
  }

  // Open date picker for an item
  function openDatePicker(item) {
    setEditingDateId(item.id);
    setEditingDateValue(item.scheduled_date);
  }

  // Close date picker
  function closeDatePicker() {
    setEditingDateId(null);
    setEditingDateValue('');
  }

  // Handle date change from picker
  function handleDatePickerChange(e) {
    setEditingDateValue(e.target.value);
  }

  // Save the new date
  function saveDateChange(itemId) {
    if (editingDateValue) {
      handleReschedule(itemId, editingDateValue);
    }
  }

  // Click outside handler for date picker
  useEffect(() => {
    function handleClickOutside(event) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        closeDatePicker();
      }
    }
    if (editingDateId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDateId]);

  // Amount editing functions
  async function handleAmountChange(instanceId, newAmount) {
    try {
      setUpdatingId(instanceId);
      
      await api.patch(`/api/forecast/${instanceId}`, { override_amount: newAmount });
      
      await loadForecast();
    } catch (err) {
      console.error('Error updating amount:', err);
      alert('Failed to update amount');
    } finally {
      setUpdatingId(null);
      setEditingAmountId(null);
    }
  }

  // Reset to original amount
  async function handleResetAmount(item) {
    try {
      setUpdatingId(item.id);
      
      await api.patch(`/api/forecast/${item.id}`, { override_amount: null });
      
      await loadForecast();
    } catch (err) {
      console.error('Error resetting amount:', err);
      alert('Failed to reset amount');
    } finally {
      setUpdatingId(null);
      setEditingAmountId(null);
    }
  }

  // Open amount editor for an item
  function openAmountEditor(item) {
    setEditingAmountId(item.id);
    const currentAmount = parseFloat(item.amount);
    // Show negative for bills/debts so it's clear it's a debit
    const displayAmount = item.item_type === 'income' ? currentAmount : -currentAmount;
    setEditingAmountValue(displayAmount.toFixed(2));
  }

  // Close amount editor
  function closeAmountEditor() {
    setEditingAmountId(null);
    setEditingAmountValue('');
  }

  // Handle amount input change
  function handleAmountInputChange(e) {
    setEditingAmountValue(e.target.value);
  }

  // Save the new amount
  function saveAmountChange(itemId) {
    const numericValue = parseFloat(editingAmountValue);
    if (!isNaN(numericValue) && numericValue !== 0) {
      // Store as positive value - item_type determines if it's income/expense
      handleAmountChange(itemId, Math.abs(numericValue));
    }
  }

  // Click outside handler for amount editor
  useEffect(() => {
    function handleClickOutside(event) {
      if (amountPickerRef.current && !amountPickerRef.current.contains(event.target)) {
        closeAmountEditor();
      }
    }
    if (editingAmountId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingAmountId]);

  // Notes editing functions
  async function handleNotesChange(instanceId, newNotes) {
    try {
      setUpdatingId(instanceId);
      
      await api.patch(`/api/forecast/${instanceId}`, { notes: newNotes || null });
      
      await loadForecast();
    } catch (err) {
      console.error('Error updating notes:', err);
      alert('Failed to update notes');
    } finally {
      setUpdatingId(null);
      setEditingNotesId(null);
    }
  }

  function openNotesEditor(item) {
    setEditingNotesId(item.id);
    setEditingNotesValue(item.notes || '');
  }

  function closeNotesEditor() {
    setEditingNotesId(null);
    setEditingNotesValue('');
  }

  function saveNotesChange(itemId) {
    handleNotesChange(itemId, editingNotesValue.trim());
  }

  // Click outside handler for notes editor
  useEffect(() => {
    function handleClickOutside(event) {
      if (notesPickerRef.current && !notesPickerRef.current.contains(event.target)) {
        closeNotesEditor();
      }
    }
    if (editingNotesId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingNotesId]);

  // Quick add functions
  function openQuickAdd(weekStartDate = null) {
    // Default to the first day of the week or today
    if (weekStartDate) {
      const weekStart = new Date(weekStartDate + 'T00:00:00');
      setQuickAddDate(weekStart.toISOString().split('T')[0]);
    } else {
      setQuickAddDate(null);
    }
    setShowQuickAdd(true);
  }

  function handleQuickAddSuccess() {
    setShowQuickAdd(false);
    setQuickAddDate(null);
    loadForecast();
  }

  function handleAddRecurringSuccess() {
    setShowAddRecurring(false);
    loadForecast();
  }

  // Click outside handler for add dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setShowAddDropdown(false);
      }
    }
    if (showAddDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddDropdown]);

  // Click outside handler for status dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownId(null);
        setConfirmDeleteId(null);
      }
    }
    if (statusDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownId]);

  // Delete one-time item
  async function handleDeleteItem(item) {
    // Only allow deleting one-time items (no recurring_item_id)
    if (item.recurring_item_id) {
      alert('Cannot delete recurring items from here. Edit the recurring item instead.');
      return;
    }
    
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete "${item.name}" from your forecast?`)) {
      return;
    }
    
    try {
      setUpdatingId(item.id);
      await api.delete(`/api/forecast/${item.id}`);
      await loadForecast();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    } finally {
      setUpdatingId(null);
    }
  }

  // Format helpers
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  // Compute local "today" once per render (reused by formatDate + overdue check)
  const now = new Date();
  const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    if (dateStr === localToday) return 'Today';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatWeekRange = (startDate) => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
  };

  // Group forecast by week
  const groupByWeek = (items) => {
    const weeks = {};
    
    items.forEach(item => {
      const date = new Date(item.scheduled_date + 'T00:00:00');
      // Get Sunday of the week
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          startDate: weekKey,
          items: [],
          weekEndBalance: null
        };
      }
      weeks[weekKey].items.push(item);
    });
    
    // Calculate week-end balance (last item's running balance in each week)
    // Sort items within each week: cleared/paid at top, then pending by date
    const statusPriority = { cleared: 0, settled: 1, skipped: 2, paid: 3, pending: 3 };
    Object.values(weeks).forEach(week => {
      week.items.sort((a, b) => {
        const aPri = statusPriority[a.status] ?? 2;
        const bPri = statusPriority[b.status] ?? 2;
        if (aPri !== bPri) return aPri - bPri;
        // Within same status group: sort by date, then sort_order, then name
        const dateCompare = (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
        if (dateCompare !== 0) return dateCompare;
        const orderCompare = (a.sort_order || 0) - (b.sort_order || 0);
        if (orderCompare !== 0) return orderCompare;
        return (a.name || '').localeCompare(b.name || '');
      });
      const pendingItems = week.items.filter(i => i.runningBalance !== null);
      if (pendingItems.length > 0) {
        week.weekEndBalance = pendingItems[pendingItems.length - 1].runningBalance;
      }
    });
    
    return weeks;
  };

  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekKey]: !prev[weekKey]
    }));
  };

  // Drag and drop handlers — row-level reordering + cross-date rescheduling
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRowDragOver = (e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItem && targetItem.id !== draggedItem.id) {
      setDropTargetId(targetItem.id);
    }
  };

  const handleRowDragLeave = (e) => {
    // Only clear if we're actually leaving the row (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetId(null);
    }
  };

  const handleRowDrop = async (e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetId(null);
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }
    
    if (draggedItem.scheduled_date === targetItem.scheduled_date) {
      // Same date — reorder: place dragged item AFTER target item
      // Get all items for this date, in current order
      const sameDateItems = forecast
        .filter(i => i.scheduled_date === targetItem.scheduled_date)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name));
      
      // Remove dragged item from list
      const withoutDragged = sameDateItems.filter(i => i.id !== draggedItem.id);
      
      // Find target position and insert after it
      const targetIndex = withoutDragged.findIndex(i => i.id === targetItem.id);
      withoutDragged.splice(targetIndex + 1, 0, draggedItem);
      
      // Send new order to backend
      const orderedIds = withoutDragged.map(i => i.id);
      try {
        setUpdatingId(draggedItem.id);
        await api.put('/api/forecast/reorder', { orderedIds });
        await loadForecast();
      } catch (err) {
        console.error('Error reordering:', err);
        alert('Failed to reorder items');
      } finally {
        setUpdatingId(null);
      }
    } else {
      // Different date — reschedule to target item's date
      handleReschedule(draggedItem.id, targetItem.scheduled_date);
    }
    
    setDraggedItem(null);
  };

  const handleWeekDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTargetId(null);
  };

  // Render balance warning banner
  const renderWarningBanner = () => {
    if (!balanceSummary) return null;
    
    if (balanceSummary.hasOverdraftRisk) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-800">Overdraft Warning</h3>
              <p className="text-base sm:text-sm text-red-700 mt-1">
                Your balance is projected to go negative ({formatCurrency(balanceSummary.firstOverdraftBalance || balanceSummary.lowestBalance)})
                on {formatDate(balanceSummary.firstOverdraftDate || balanceSummary.lowestBalanceDate)} after "{balanceSummary.firstOverdraftItem || balanceSummary.lowestBalanceItem}".
                {balanceSummary.lowestBalance < (balanceSummary.firstOverdraftBalance || 0) && (
                  <> Lowest point: {formatCurrency(balanceSummary.lowestBalance)} on {formatDate(balanceSummary.lowestBalanceDate)}.</>
                )}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    if (balanceSummary.hasBufferWarning) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">Low Balance Warning</h3>
              <p className="text-base sm:text-sm text-amber-700 mt-1">
                Your balance will drop below your minimum buffer to {formatCurrency(balanceSummary.lowestBalance)} 
                on {formatDate(balanceSummary.lowestBalanceDate)}.
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Render balance summary cards
  const renderBalanceSummary = () => {
    if (!balanceSummary) return null;
    
    // Compute data freshness from accountsData
    const getDataFreshness = () => {
      const accounts = Object.values(accountsData || {});
      if (accounts.length === 0) return null;
      // If filtering by account, use that account's timestamp; otherwise use the oldest
      const relevantAccounts = selectedAccountId 
        ? accounts.filter(a => String(a.accountId) === String(selectedAccountId))
        : accounts;
      const timestamps = relevantAccounts
        .map(a => a.plaidBalanceUpdatedAt)
        .filter(Boolean)
        .map(t => new Date(t));
      if (timestamps.length === 0) return null;
      const oldest = new Date(Math.min(...timestamps));
      const now = new Date();
      const diffMs = now - oldest;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHrs = Math.floor(diffMin / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      return `${diffDays}d ago`;
    };
    const freshness = getDataFreshness();
    const isStale = (() => {
      const accounts = Object.values(accountsData || {});
      const timestamps = accounts.map(a => a.plaidBalanceUpdatedAt).filter(Boolean).map(t => new Date(t));
      if (timestamps.length === 0) return false;
      const oldest = new Date(Math.min(...timestamps));
      return (new Date() - oldest) > 4 * 60 * 60 * 1000; // > 4 hours
    })();
    
    return (
      <div className="mb-4 sm:mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow border border-gray-200">
          <p className="text-base sm:text-sm text-gray-500 mb-1">Starting Balance</p>
          <p className="text-base sm:text-xl font-semibold text-gray-900">
            {formatCurrency(balanceSummary.startingBalance)}
          </p>
          {balanceSummary.plaidCurrentBalance != null && (
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
              <p className="text-xs text-gray-400">
                Current: <span className="text-gray-600 font-medium">{formatCurrency(balanceSummary.plaidCurrentBalance)}</span>
              </p>
              {balanceSummary.plaidAvailableBalance != null && (
                <p className="text-xs text-gray-400">
                  Available: <span className="text-gray-600 font-medium">{formatCurrency(balanceSummary.plaidAvailableBalance)}</span>
                </p>
              )}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow border border-gray-200">
          <p className="text-base sm:text-sm text-gray-500 mb-1">Ending Balance</p>
          <p className={`text-base sm:text-xl font-semibold ${
            balanceSummary.endingBalance < 0 ? 'text-red-600' : 'text-gray-900'
          }`}>
            {formatCurrency(balanceSummary.endingBalance)}
          </p>
        </div>
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow border border-gray-200">
          <p className="text-base sm:text-sm text-gray-500 mb-1">Lowest Point</p>
          <p className={`text-base sm:text-xl font-semibold ${
            balanceSummary.lowestBalance < 0 ? 'text-red-600' :
            balanceSummary.lowestBalance < balanceSummary.minimumBuffer ? 'text-amber-600' : 'text-gray-900'
          }`}>
            {formatCurrency(balanceSummary.lowestBalance)}
          </p>
          {balanceSummary.lowestBalanceDate && (
            <p className="text-xs text-gray-400 mt-1">
              {formatDate(balanceSummary.lowestBalanceDate)}
            </p>
          )}
        </div>
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow border border-gray-200">
          <p className="text-base sm:text-sm text-gray-500 mb-1">Min Buffer</p>
          <p className="text-base sm:text-xl font-semibold text-gray-900">
            {formatCurrency(balanceSummary.minimumBuffer)}
          </p>
        </div>
        </div>
        {freshness && (
          <div className={`flex items-center gap-1.5 mt-2 text-xs ${isStale ? 'text-amber-600' : 'text-gray-400'}`}>
            <Clock className="w-3 h-3" />
            <span>Bank data as of {freshness}</span>
            {isStale && <span className="font-medium">(may be stale — banks typically update 1-4x daily, often between 2-6 AM)</span>}
          </div>
        )}
      </div>
    );
  };

  // Build account lookup map for displaying account names on items
  const accountMap = React.useMemo(() => {
    const map = {};
    accountsList.forEach(acc => { map[acc.id] = acc; });
    return map;
  }, [accountsList]);

  // Build category lookup map (case-insensitive + snake_case normalization)
  const categoryMap = React.useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.name.toLowerCase()] = cat;
      map[cat.name.toLowerCase().replace(/\s+/g, '_')] = cat;
    });
    return map;
  }, [categories]);

  const renderCategoryBadge = React.useCallback((category) => {
    if (!category) return null;
    // If already an object with name (future-proofing)
    if (typeof category === 'object' && category.name) {
      return (
        <span
          className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{
            backgroundColor: category.color ? `${category.color}20` : '#e5e7eb',
            color: category.color || '#6b7280'
          }}
        >
          {category.name}
        </span>
      );
    }
    if (typeof category !== 'string') return null;
    const match = categoryMap[category.toLowerCase()];
    const displayName = match?.name || category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const color = match?.color || null;
    return (
      <span
        className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
        style={{
          backgroundColor: color ? `${color}20` : '#e5e7eb',
          color: color || '#6b7280'
        }}
      >
        {displayName}
      </span>
    );
  }, [categoryMap]);

  // Filter accounts dropdown based on entity selection
  const filteredAccountsList = React.useMemo(() => {
    if (activeFilter === 'all') return accountsList;
    if (activeFilter === 'personal') {
      // Personal = accounts NOT linked to any business entity
      const allLinkedIds = new Set();
      entities.forEach(e => (e.linkedAccountIds || []).forEach(id => allLinkedIds.add(id)));
      return accountsList.filter(acc => !allLinkedIds.has(acc.id));
    }
    // Specific entity — show only accounts linked to that entity
    const entity = entities.find(e => e.id === activeFilter);
    if (!entity) return accountsList;
    const linkedIds = new Set(entity.linkedAccountIds || []);
    return accountsList.filter(acc => linkedIds.has(acc.id));
  }, [accountsList, activeFilter, entities]);

  // Reset account selection when entity filter changes and selected account is no longer visible
  React.useEffect(() => {
    if (selectedAccountId && filteredAccountsList.length > 0) {
      const stillVisible = filteredAccountsList.some(acc => String(acc.id) === String(selectedAccountId));
      if (!stillVisible) {
        setSelectedAccountId('');
      }
    }
  }, [filteredAccountsList, selectedAccountId]);

  // Shared date picker popover
  const renderDatePicker = (item) => {
    if (editingDateId !== item.id) return null;
    return (
      <div ref={datePickerRef} className="absolute left-0 top-full mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3">
        <div className="flex flex-col gap-2">
          <input
            type="date"
            value={editingDateValue}
            onChange={handleDatePickerChange}
            className="text-base sm:text-sm border border-gray-300 rounded px-2 py-1.5 sm:py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            autoFocus
          />
          <div className="flex gap-1">
            <button
              onClick={() => saveDateChange(item.id)}
              className="flex-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 min-h-[44px] sm:min-h-0"
            >
              Save
            </button>
            <button
              onClick={closeDatePicker}
              className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 min-h-[44px] sm:min-h-0 flex items-center justify-center"
            >
              <X className="w-4 h-4 sm:w-3 sm:h-3" />
            </button>
          </div>
          {item.original_date && item.original_date !== item.scheduled_date && (
            <button
              onClick={() => handleResetToOriginal(item)}
              className="flex items-center justify-center gap-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs text-amber-600 hover:bg-amber-50 rounded min-h-[44px] sm:min-h-0"
            >
              <RotateCcw className="w-3 h-3" />
              Reset to {formatDate(item.original_date)}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Shared amount editor popover
  const renderAmountEditor = (item, hasOverride, originalAmount) => {
    if (editingAmountId !== item.id) return null;
    return (
      <div ref={amountPickerRef} className="absolute z-50 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">$</span>
            <input
              type="number"
              step="0.01"
              value={editingAmountValue}
              onChange={handleAmountInputChange}
              className="w-28 sm:w-24 text-base sm:text-sm border border-gray-300 rounded px-2 py-1.5 sm:py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => saveAmountChange(item.id)}
              className="flex-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 min-h-[44px] sm:min-h-0"
            >
              Save
            </button>
            <button
              onClick={closeAmountEditor}
              className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 min-h-[44px] sm:min-h-0 flex items-center justify-center"
            >
              <X className="w-4 h-4 sm:w-3 sm:h-3" />
            </button>
          </div>
          {hasOverride && (
            <button
              onClick={() => handleResetAmount(item)}
              className="flex items-center justify-center gap-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs text-amber-600 hover:bg-amber-50 rounded min-h-[44px] sm:min-h-0"
            >
              <RotateCcw className="w-3 h-3" />
              Reset to {formatCurrency(Math.abs(originalAmount))}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Shared notes editor popover
  const renderNotesEditor = (item) => {
    if (editingNotesId !== item.id) return null;
    return (
      <div ref={notesPickerRef} className="absolute z-50 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-72 sm:w-64">
        <div className="flex flex-col gap-2">
          <textarea
            value={editingNotesValue}
            onChange={(e) => setEditingNotesValue(e.target.value)}
            placeholder="Add a note or reminder..."
            rows={3}
            className="text-base sm:text-sm border border-gray-300 rounded px-2 py-1.5 sm:py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            autoFocus
          />
          <div className="flex gap-1">
            <button
              onClick={() => saveNotesChange(item.id)}
              className="flex-1 px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 min-h-[44px] sm:min-h-0"
            >
              Save
            </button>
            <button
              onClick={closeNotesEditor}
              className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 min-h-[44px] sm:min-h-0 flex items-center justify-center"
            >
              <X className="w-4 h-4 sm:w-3 sm:h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Status pill with dropdown
  const renderStatusPill = (item, isUpdating) => {
    const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    const isOpen = statusDropdownId === item.id;
    const isConfirmingDelete = confirmDeleteId === item.id;
    const canDelete = !item.recurring_item_id;

    // Cleared — auto-cleared (has Plaid link) shows unmatch; manually-cleared reverts to settled
    if (item.status === 'cleared') {
      const isAutoCleared = !!item.plaid_transaction_id;
      return (
        <button
          type="button"
          onClick={() => isAutoCleared ? handleUnclear(item) : handleStatusChange(item.id, 'settled')}
          disabled={isUpdating}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium min-h-[44px] sm:min-h-[32px] hover:bg-blue-200 transition-colors"
          title={isAutoCleared ? 'Click to undo bank match' : 'Click to revert to settled'}
          aria-label={isAutoCleared ? 'Undo bank match' : 'Revert to settled'}
        >
          <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
          Cleared
          {isAutoCleared && <Unlink className="w-3 h-3 ml-1 opacity-60" aria-hidden="true" />}
        </button>
      );
    }

    return (
      <div className="relative" ref={isOpen ? statusDropdownRef : null}>
        <button
          onClick={() => setStatusDropdownId(isOpen ? null : item.id)}
          disabled={isUpdating}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all min-h-[44px] sm:min-h-[32px] ${config.bg} ${config.color} ring-1 ${config.border} hover:ring-2`}
        >
          <Icon className="w-3 h-3" />
          {config.label}
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            {['pending', 'paid', 'settled', 'skipped'].map(status => {
              const opt = STATUS_CONFIG[status];
              const OptIcon = opt.icon;
              const isActive = item.status === status;
              return (
                <button
                  key={status}
                  onClick={() => {
                    if (!isActive) handleStatusChange(item.id, status);
                    setStatusDropdownId(null);
                    setConfirmDeleteId(null);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-base sm:text-sm text-left transition-colors min-h-[44px] sm:min-h-0 ${
                    isActive ? `${opt.bg} ${opt.color} font-medium` : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <OptIcon className="w-4 h-4 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="block">{opt.label}</span>
                    {opt.tooltip && (
                      <span className="block text-xs text-gray-400 truncate">{opt.tooltip.split(': ').slice(1).join(': ')}</span>
                    )}
                  </div>
                  {isActive && <CheckCircle2 className="w-3 h-3 ml-auto flex-shrink-0" />}
                </button>
              );
            })}
            {item.status === 'settled' && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => {
                    handleStatusChange(item.id, 'cleared');
                    setStatusDropdownId(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-base sm:text-sm text-left transition-colors min-h-[44px] sm:min-h-0 text-blue-700 hover:bg-blue-50"
                >
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="block font-medium">Confirm Cleared</span>
                    <span className="block text-xs text-gray-400">Bank already processed this</span>
                  </div>
                </button>
              </>
            )}
            {canDelete && (
              <>
                <div className="border-t border-gray-100 my-1" />
                {isConfirmingDelete ? (
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-500 mb-2">Delete this item?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          handleDeleteItem(item);
                          setStatusDropdownId(null);
                          setConfirmDeleteId(null);
                        }}
                        className="flex-1 px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 min-h-[44px] sm:min-h-0"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 min-h-[44px] sm:min-h-0"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(item.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-base sm:text-sm text-left text-red-600 hover:bg-red-50 transition-colors min-h-[44px] sm:min-h-0"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render a single forecast item row
  const renderForecastItem = (item) => {
    const typeConfig = TYPE_CONFIG[item.item_type] || TYPE_CONFIG.bill;

    // Determine effective status - show "overdue" for past pending items
    const isOverdue = item.status === 'pending' && item.scheduled_date < localToday;
    // Stale settled: settled for >14 days past scheduled date — needs user attention
    const isStaleSettled = item.status === 'settled' && (() => {
      const scheduled = new Date(item.scheduled_date + 'T00:00:00');
      return Math.floor((new Date() - scheduled) / 86400000) > 14;
    })();
    const effectiveStatus = isOverdue ? 'overdue' : item.status;
    const statusConfig = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.pending;
    const TypeIcon = typeConfig.icon;

    // Detect override: amount differs from recurring item's original amount
    const originalAmount = item.recurringItem?.amount;
    const hasOverride = originalAmount !== undefined &&
      parseFloat(item.amount) !== parseFloat(originalAmount);
    const effectiveAmount = parseFloat(item.amount);

    const isUpdating = updatingId === item.id;
    const isDragging = draggedItem?.id === item.id;
    const isDropTarget = dropTargetId === item.id;

    const dragProps = {
      draggable: item.status === 'pending',
      onDragStart: (e) => handleDragStart(e, item),
      onDragOver: (e) => handleRowDragOver(e, item),
      onDragLeave: handleRowDragLeave,
      onDrop: (e) => handleRowDrop(e, item),
      onDragEnd: handleDragEnd,
    };

    const hasOpenDropdown = statusDropdownId === item.id;
    const baseClasses = `
      ${isDragging ? 'opacity-50' : ''}
      ${isUpdating ? 'opacity-70 pointer-events-none' : ''}
      ${!hasOpenDropdown && item.status !== 'pending' && item.status !== 'paid' && item.status !== 'settled' ? 'opacity-60' : ''}
      ${isDropTarget ? 'border-b-2 border-b-emerald-500' : ''}
      ${hasOpenDropdown ? 'z-40 relative' : ''}
    `;

    const overdueStyle = isOverdue
      ? { boxShadow: 'inset 4px 0 0 0 #f87171', backgroundColor: '#fef2f2' }
      : isStaleSettled
        ? { boxShadow: 'inset 4px 0 0 0 #f59e0b', backgroundColor: '#fffbeb' }
        : {};

    return (
      <React.Fragment key={item.id}>
        {/* === MOBILE CARD VIEW (< lg) === */}
        <div
          {...dragProps}
          className={`lg:hidden group p-3 bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors ${baseClasses}`}
          style={overdueStyle}
        >
          {/* Top row: type icon + name + amount */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg ${typeConfig.bg} flex-shrink-0`}>
                <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-base font-medium text-gray-900 truncate">{item.name}</p>
                  {!item.recurring_item_id && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded flex-shrink-0">One-time</span>
                  )}
                  {item.is_transfer && (
                    <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded flex-shrink-0">
                      <ArrowLeftRight className="w-3 h-3" />
                    </span>
                  )}
                  {item.is_envelope && (
                    <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded flex-shrink-0">Budget</span>
                  )}
                  {parseInt(item.receipt_count) > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setReceiptViewerTxId(item.id); }}
                      className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded flex-shrink-0 hover:bg-amber-200 min-h-[44px] sm:min-h-0"
                      title="View receipt"
                    >
                      <Camera className="w-3 h-3" />
                      Receipt
                    </button>
                  )}
                </div>
                {item.recurringItem?.frequency && (
                  <p className="text-xs text-gray-400 capitalize">{item.recurringItem.frequency}</p>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0 relative">
              {renderAmountEditor(item, hasOverride, originalAmount)}
              <button
                onClick={() => item.status === 'pending' && openAmountEditor(item)}
                disabled={item.status !== 'pending'}
                className={`text-right min-h-[44px] sm:min-h-0 ${item.status === 'pending' ? 'cursor-pointer active:bg-gray-100 rounded px-1' : ''}`}
              >
                <span className={`text-base font-semibold ${typeConfig.amountColor}`}>
                  {item.item_type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(effectiveAmount))}
                </span>
                {hasOverride && (
                  <span className="block text-xs text-gray-400 line-through">
                    {formatCurrency(Math.abs(originalAmount))}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Middle row: date + balance + category */}
          <div className="flex items-center justify-between gap-2 mb-2 text-base sm:text-sm">
            <div className="flex items-center gap-2 relative">
              {renderDatePicker(item)}
              <button
                onClick={() => item.status === 'pending' && openDatePicker(item)}
                disabled={item.status !== 'pending'}
                className={`text-left min-h-[44px] sm:min-h-0 ${item.status === 'pending' ? 'cursor-pointer active:bg-gray-100 rounded px-1' : ''}`}
              >
                <span className={`text-base sm:text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{formatDate(item.scheduled_date)}</span>
                {isOverdue && <span className="ml-1 text-xs text-red-600 font-medium">Overdue</span>}
                {isStaleSettled && <span className="ml-1 text-xs text-amber-600 font-medium">Stale — confirm cleared?</span>}
                {!isOverdue && item.original_date && item.original_date !== item.scheduled_date && (
                  <span className="ml-1 text-xs text-amber-600">Moved</span>
                )}
              </button>
              {renderCategoryBadge(item.category)}
            </div>
            <div className="text-right flex-shrink-0">
              {item.runningBalance !== null ? (
                <span className={`text-base sm:text-sm font-semibold ${
                  item.runningBalance < 0 ? 'text-red-600' :
                  item.overdraftWarning ? 'text-amber-600' : 'text-gray-700'
                }`}>
                  Bal: {formatCurrency(item.runningBalance)}
                  {item.overdraftWarning && <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />}
                </span>
              ) : (
                <span className="text-base sm:text-sm text-gray-400">--</span>
              )}
            </div>
          </div>

          {/* Envelope progress (mobile) */}
          {item.is_envelope && envelopeSpending[item.id] && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    envelopeSpending[item.id].percentUsed >= 90 ? 'bg-red-500' :
                    envelopeSpending[item.id].percentUsed >= 70 ? 'bg-amber-500' : 'bg-violet-500'
                  }`}
                  style={{ width: `${envelopeSpending[item.id].percentUsed}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${
                envelopeSpending[item.id].percentUsed >= 90 ? 'text-red-600' :
                envelopeSpending[item.id].percentUsed >= 70 ? 'text-amber-600' : 'text-violet-600'
              }`}>
                ${envelopeSpending[item.id].spent.toFixed(0)} / ${envelopeSpending[item.id].budgeted.toFixed(0)}
              </span>
            </div>
          )}

          {/* Notes (mobile) */}
          <div className="relative">
          {renderNotesEditor(item)}
          </div>
          {editingNotesId !== item.id && item.notes && (
            <button
              onClick={() => openNotesEditor(item)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mb-2 min-h-[44px] sm:min-h-0"
              title={item.notes}
            >
              <FileText className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{item.notes}</span>
            </button>
          )}

          {/* Bottom row: account + status buttons */}
          <div className="flex items-center justify-between">
            {item.bank_account_id && accountMap[item.bank_account_id] ? (
              <span className="text-xs text-gray-400 flex items-center gap-1 truncate mr-2">
                <Building2 className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{accountMap[item.bank_account_id].displayName}</span>
              </span>
            ) : <div />}
            <div className="ml-auto">
              {renderStatusPill(item, isUpdating)}
            </div>
          </div>
        </div>

        {/* === DESKTOP ROW VIEW (>= lg) === */}
        <div
          {...dragProps}
          className={`hidden lg:flex group items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors ${baseClasses}`}
          style={overdueStyle}
        >
          {/* Drag handle */}
          <div className={`w-4 flex-shrink-0 ${item.status === 'pending' ? 'cursor-grab text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity' : ''}`}>
            {item.status === 'pending' && <GripVertical className="w-4 h-4" />}
          </div>

          {/* Date */}
          <div className="w-28 flex-shrink-0 relative">
            {renderDatePicker(item)}
            <button
              onClick={() => item.status === 'pending' && openDatePicker(item)}
              disabled={item.status !== 'pending'}
              className={`text-left ${item.status === 'pending' ? 'cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1' : ''}`}
            >
              <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{formatDate(item.scheduled_date)}</span>
              {isOverdue && (
                <span className="block text-xs text-red-600 font-medium">Overdue</span>
              )}
              {isStaleSettled && (
                <span className="block text-xs text-amber-600 font-medium">Stale — confirm cleared?</span>
              )}
              {!isOverdue && item.original_date && item.original_date !== item.scheduled_date && (
                <span className="block text-xs text-amber-600">Rescheduled</span>
              )}
            </button>
          </div>

          {/* Name (with type icon inline) */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-lg ${typeConfig.bg} flex-shrink-0`}>
                <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
              </div>
              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
              {!item.recurring_item_id && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">One-time</span>
              )}
              {item.plaid_transaction_id && (
                <Building2 className="w-3 h-3 text-blue-500" title="Linked to bank transaction" />
              )}
              {item.is_transfer && (
                <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded" title="Inter-entity transfer">
                  <ArrowLeftRight className="w-3 h-3" />
                  Transfer
                </span>
              )}
              {item.is_envelope && (
                <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded" title="Budget envelope">
                  Budget
                </span>
              )}
              {parseInt(item.receipt_count) > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setReceiptViewerTxId(item.id); }}
                  className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                  title="View receipt"
                >
                  <Camera className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 ml-9">
              {renderCategoryBadge(item.category)}
              {item.recurringItem?.frequency && (
                <p className="text-xs text-gray-400 capitalize">{item.recurringItem.frequency}</p>
              )}
              {item.bank_account_id && accountMap[item.bank_account_id] && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  {item.recurringItem?.frequency && <span>·</span>}
                  <Building2 className="w-3 h-3" />
                  {accountMap[item.bank_account_id].displayNameWithInstitution || accountMap[item.bank_account_id].displayName}
                </span>
              )}
              {item.is_envelope && envelopeSpending[item.id] && (
                <div className="flex items-center gap-2 ml-1">
                  <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        envelopeSpending[item.id].percentUsed >= 90 ? 'bg-red-500' :
                        envelopeSpending[item.id].percentUsed >= 70 ? 'bg-amber-500' : 'bg-violet-500'
                      }`}
                      style={{ width: `${envelopeSpending[item.id].percentUsed}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    envelopeSpending[item.id].percentUsed >= 90 ? 'text-red-600' :
                    envelopeSpending[item.id].percentUsed >= 70 ? 'text-amber-600' : 'text-violet-600'
                  }`}>
                    ${envelopeSpending[item.id].spent.toFixed(0)} / ${envelopeSpending[item.id].budgeted.toFixed(0)}
                  </span>
                </div>
              )}
              {/* Notes indicator/button */}
              <div className="relative">
              {renderNotesEditor(item)}
              </div>
              {editingNotesId !== item.id && (item.notes ? (
                <button
                  onClick={() => openNotesEditor(item)}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  title={item.notes}
                >
                  <FileText className="w-3 h-3" />
                  <span className="truncate max-w-[100px]">{item.notes}</span>
                </button>
              ) : (
                <button
                  onClick={() => openNotesEditor(item)}
                  className="text-xs text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  + Add note
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div className="w-28 text-right flex-shrink-0 relative">
            {renderAmountEditor(item, hasOverride, originalAmount)}
            <button
              onClick={() => item.status === 'pending' && openAmountEditor(item)}
              disabled={item.status !== 'pending'}
              className={`text-right w-full ${item.status === 'pending' ? 'cursor-pointer hover:bg-gray-100 rounded px-1' : ''}`}
            >
              <span className={`text-sm font-medium ${typeConfig.amountColor}`}>
                {item.item_type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(effectiveAmount))}
              </span>
              {hasOverride && (
                <span className="block text-xs text-gray-400 line-through">
                  {formatCurrency(Math.abs(originalAmount))}
                </span>
              )}
            </button>
          </div>

          {/* Running Balance */}
          <div className="w-28 text-right flex-shrink-0">
            {item.runningBalance !== null ? (
              <span className={`text-sm font-semibold ${
                item.runningBalance < 0 ? 'text-red-600' :
                item.overdraftWarning ? 'text-amber-600' : 'text-gray-700'
              }`}>
                {formatCurrency(item.runningBalance)}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
            {item.overdraftWarning && (
              <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />
            )}
          </div>

          {/* Status */}
          <div className="w-32 flex-shrink-0 flex items-center justify-end">
            {renderStatusPill(item, isUpdating)}
          </div>
        </div>
      </React.Fragment>
    );
  };

  // Render week section
  const renderWeekSection = (weekKey, weekData) => {
    const isExpanded = expandedWeeks[weekKey] ?? true;
    const hasWarning = weekData.items.some(i => i.overdraftWarning);
    
    return (
      <div 
        key={weekKey} 
        className="mb-4"
        onDragOver={handleWeekDragOver}
      >
        {/* Week header */}
        <div
          className={`
            w-full flex flex-col lg:flex-row lg:items-center lg:justify-between px-3 lg:px-4 py-3 gap-1 lg:gap-0
            bg-gray-100 hover:bg-gray-200 rounded-t-lg transition-colors
            ${!isExpanded ? 'rounded-b-lg' : ''}
          `}
        >
          <button
            onClick={() => toggleWeek(weekKey)}
            className="flex items-center gap-2 lg:gap-3 min-w-0 min-h-[44px] sm:min-h-0"
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
            )}
            <span className="font-medium text-gray-700 text-base truncate">
              Week of {formatWeekRange(weekKey)}
            </span>
            {hasWarning && (
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            )}
          </button>
          <div className="flex items-center gap-2 lg:gap-4 text-base sm:text-sm ml-7 lg:ml-0">
            <span className="text-gray-500">
              {weekData.items.length} item{weekData.items.length !== 1 ? 's' : ''}
            </span>
            {weekData.weekEndBalance !== null && (
              <span className={`font-medium ${
                weekData.weekEndBalance < 0 ? 'text-red-600' : 'text-gray-700'
              }`}>
                <span className="hidden lg:inline">Week End: </span>{formatCurrency(weekData.weekEndBalance)}
              </span>
            )}
            {/* Quick add button for this week */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                openQuickAdd(weekKey);
              }}
              title="Add one-time item to this week"
              className="p-2 lg:p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors min-w-[44px] min-h-[44px] lg:min-w-0 lg:min-h-0 flex items-center justify-center"
            >
              <Plus className="w-5 h-5 lg:w-4 lg:h-4" />
            </button>
          </div>
        </div>
        
        {/* Week items */}
        {isExpanded && (
          <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0">
            {/* Header row - desktop only */}
            <div className="hidden lg:flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="w-4"></div> {/* Drag handle space */}
              <div className="w-28 flex-shrink-0">Date</div>
              <div className="flex-1">Description</div>
              <div className="w-28 text-right flex-shrink-0">Amount</div>
              <div className="w-28 text-right flex-shrink-0">Balance</div>
              <div className="w-32 text-right flex-shrink-0">Status</div>
            </div>
            
            {/* Items */}
            {weekData.items.map(item => renderForecastItem(item))}
          </div>
        )}
      </div>
    );
  };

  // Main render
  const weeks = groupByWeek(forecast);
  const sortedWeekKeys = Object.keys(weeks).sort();

  return (
    <AppShell>
      <div className="p-4 lg:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">8-Week Forecast</h1>
            <p className="text-gray-500 text-base sm:text-sm mt-1">
              Projected cash flow based on your recurring items
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadForecast(true)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm min-h-[44px]"
              title="Rebuild forecast from recurring items"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Regenerate</span>
            </button>

            {/* Add dropdown */}
            <div className="relative" ref={addDropdownRef}>
              <button
                onClick={() => setShowAddDropdown(prev => !prev)}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium min-h-[44px]"
              >
                <Plus className="w-4 h-4" />
                Add
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {/* Desktop dropdown */}
              {showAddDropdown && (
                <>
                  {/* Desktop floating dropdown */}
                  <div className="hidden sm:block absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => { setShowAddDropdown(false); openQuickAdd(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Zap className="w-4 h-4 text-orange-500" />
                      <div className="text-left">
                        <div className="font-medium">One-Time Entry</div>
                        <div className="text-xs text-gray-500">Single entry in forecast</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowAddDropdown(false); setShowAddRecurring(true); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Repeat className="w-4 h-4 text-emerald-500" />
                      <div className="text-left">
                        <div className="font-medium">Recurring Bill/Income</div>
                        <div className="text-xs text-gray-500">Repeating series with forecast</div>
                      </div>
                    </button>
                  </div>

                  {/* Mobile bottom sheet */}
                  <div className="sm:hidden fixed inset-0 z-50">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddDropdown(false)} />
                    <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 z-50" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}>
                      <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
                      <button
                        onClick={() => { setShowAddDropdown(false); openQuickAdd(); }}
                        className="w-full flex items-center gap-4 px-4 py-4 text-left rounded-lg hover:bg-gray-50 transition-colors min-h-[56px]"
                      >
                        <Zap className="w-5 h-5 text-orange-500 flex-shrink-0" />
                        <div>
                          <div className="text-base font-medium text-gray-900">One-Time Entry</div>
                          <div className="text-base sm:text-sm text-gray-500">Single entry in forecast</div>
                        </div>
                      </button>
                      <button
                        onClick={() => { setShowAddDropdown(false); setShowAddRecurring(true); }}
                        className="w-full flex items-center gap-4 px-4 py-4 text-left rounded-lg hover:bg-gray-50 transition-colors min-h-[56px]"
                      >
                        <Repeat className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <div>
                          <div className="text-base font-medium text-gray-900">Recurring Bill/Income</div>
                          <div className="text-base sm:text-sm text-gray-500">Repeating series with forecast</div>
                        </div>
                      </button>
                      <button
                        onClick={() => setShowAddDropdown(false)}
                        className="w-full mt-2 py-3 text-base font-medium text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-base sm:text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Loading state */}
        {loading && forecast.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Account filter - always visible when accounts exist */}
        {!loading && filteredAccountsList.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <label htmlFor="account-filter" className="text-base sm:text-sm font-medium text-gray-700">
                Account:
              </label>
            </div>
            <select
              id="account-filter"
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 min-h-[44px] sm:min-h-0"
            >
              <option value="">All Accounts</option>
              {filteredAccountsList.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.displayNameWithInstitution || acc.displayName}
                </option>
              ))}
            </select>
            {selectedAccountId && (
              <button
                onClick={() => setSelectedAccountId('')}
                className="text-base sm:text-xs text-gray-500 hover:text-gray-700 min-h-[44px] sm:min-h-0"
              >
                Clear filter
              </button>
            )}
          </div>
        )}

        {/* Content */}
        {!loading && forecast.length === 0 && !error && (
          <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-200">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {selectedAccountId ? 'No forecast items for this account' : 'No forecast data'}
            </h3>
            <p className="text-base sm:text-sm text-gray-500 mb-4">
              {selectedAccountId
                ? 'Try selecting a different account or view all accounts'
                : 'Add recurring items to see your 8-week cash flow forecast'
              }
            </p>
            {selectedAccountId ? (
              <button
                onClick={() => setSelectedAccountId('')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors min-h-[44px] sm:min-h-0"
              >
                View All Accounts
              </button>
            ) : (
              <button
                onClick={() => setShowAddRecurring(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors min-h-[44px] sm:min-h-0"
              >
                <Plus className="w-4 h-4" />
                Add Recurring Item
              </button>
            )}
          </div>
        )}

        {forecast.length > 0 && (
          <>
            {/* Warning banner */}
            {renderWarningBanner()}
            
            {/* Balance summary cards */}
            {renderBalanceSummary()}
            
            {/* Week sections */}
            <div className="space-y-4">
              {sortedWeekKeys.map(weekKey => {
                // Hide past weeks where all items are resolved (no unconfirmed items remain)
                // "paid" and "settled" items need user attention — keep their week visible
                const isPastWeek = weekKey < localToday;
                const allResolved = weeks[weekKey].items.every(i =>
                  i.status !== 'pending' && i.status !== 'paid' && i.status !== 'settled'
                );
                if (isPastWeek && allResolved) return null;
                return renderWeekSection(weekKey, weeks[weekKey]);
              })}
            </div>
            
            {/* Legend */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="text-base sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">Legend</h4>
              <div className="flex flex-wrap gap-3 sm:gap-6 text-base sm:text-sm">
                <div className="flex items-center gap-2">
                  <Circle className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Pending</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-gray-600">Paid</span>
                </div>
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-amber-600" />
                  <span className="text-gray-600">Skipped</span>
                </div>
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Drag to reschedule</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">One-time</span>
                  <span className="text-gray-600">Manual entry</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Quick Add Modal */}
      {showQuickAdd && (
        <QuickAddModal
          onClose={() => setShowQuickAdd(false)}
          onSuccess={handleQuickAddSuccess}
          prefilledDate={quickAddDate}
          defaultBankAccountId={selectedAccountId || null}
        />
      )}

      {/* Add Recurring Modal */}
      {showAddRecurring && (
        <AddRecurringModal
          selectedAccountId={selectedAccountId || null}
          accountsList={accountsList}
          onClose={() => setShowAddRecurring(false)}
          onSuccess={handleAddRecurringSuccess}
        />
      )}
      {receiptViewerTxId && (
        <ReceiptViewer
          scheduledTransactionId={receiptViewerTxId}
          onClose={() => setReceiptViewerTxId(null)}
        />
      )}
    </AppShell>
  );
}
